/**
 * Voice → task draft, resolved ENTIRELY in the browser (prototype, 2026-10-08).
 *
 * The transcript comes from the browser's ON-DEVICE speech recognition (TaskComposer sets
 * `processLocally = true`, which forbids a cloud fallback). This file turns that transcript into
 * the same editor HTML a typed task produces: staff become @ badges, patients become # badges.
 * Dates stay as words — the composer's chrono-simple pass reads "by Friday" the same way it does
 * for typed text. Filler words ("um") and the spoken instruction around the task ("assign Nilay a
 * task to …") are dropped; the person named in the instruction stays assigned via their @ badge. Nothing here talks to the network; the inputs are the employee list and the
 * patient roster the composer already holds in memory.
 *
 * Matching is deliberately conservative — a wrong patient tag is worse than none. The draft is
 * always shown for review before the user presses Enter, and an ambiguous name is LEFT AS TEXT
 * with its candidates returned so the composer can say so.
 *
 * KEEP THIS FILE IMPORT-FREE: cypress/node-tests/voice-task.test.js executes it directly.
 */

const HONORIFICS = new Set(['mr', 'mrs', 'ms', 'miss', 'mister', 'missus', 'dr', 'doctor']);
const PATIENT_CUES = new Set(['patient', 'patients']);
const NAMED_WORDS = new Set(['named', 'called']); // "the patient named Justin"
// A staff FIRST name on its own only counts after one of these ("remind Linda", "assign to Linda").
// Without a cue a lone first name is too likely to be a patient's.
const STAFF_CUES = new Set(['assign', 'assigned', 'to', 'for', 'remind', 'tell', 'ask', 'have', 'let']);
// Edit distance 1 is allowed on surnames this long or longer (speech garbles a letter, not a name).
const FUZZY_MIN_LEN = 5;
// How many people an ambiguous name offers as one-click picks (the rest: tag with # / @).
const MAX_OPTIONS = 6;

const patientLabel = (p) => p.PatientWithDOB || `${p.LName}, ${p.FName}`;

/** Lower-case, drop a trailing possessive, keep letters and digits only ("O'Brien's" → "obrien"). */
export function normalizeWord(word) {
  return String(word == null ? '' : word)
    .toLowerCase()
    .replace(/['’]s$/, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * A rough "sounds like" key for NAMES: first sound + the consonant skeleton, vowels dropped, doubles
 * collapsed. "Nilay", "Neelay", "Nile", "Neil" → "nl". Used only for STAFF first names, only after a
 * cue word, and only when exactly one staff member has that key — the recognizer spells unusual
 * names creatively ("Nilay" came back as something else on 2026-10-08).
 */
export function soundKey(word) {
  let w = normalizeWord(word).replace(/[0-9]/g, '');
  if (!w) return '';
  w = w.replace(/^kn/, 'n').replace(/^wr/, 'r').replace(/ph/g, 'f').replace(/ck/g, 'k').replace(/q/g, 'k')
    .replace(/x/g, 'ks').replace(/z/g, 's').replace(/c(?=[eiy])/g, 's').replace(/c/g, 'k').replace(/v/g, 'f')
    .replace(/gh/g, 'g').replace(/th/g, 't').replace(/dg/g, 'j');
  const head = /[aeiouy]/.test(w[0]) ? 'a' : w[0];
  return (head + w.slice(1).replace(/[aeiouyhw]/g, '')).replace(/(.)\1+/g, '$1');
}

function isCapitalised(raw) {
  return /^[A-Z]/.test(String(raw || '').replace(/^[^A-Za-z]+/, ''));
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Levenshtein distance, bailing out early once it exceeds `max`. */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

/** The same structure the @ picker embeds (TaskComposer.vue embeddedItem slot, rendered). */
export function buildEmployeeBadgeHTML({ EmployeeID, Name }) {
  const id = String(EmployeeID == null ? '' : EmployeeID).trim();
  // The read-back regex (taskMentions.js) only recognises [a-zA-Z0-9-]; anything else is refused.
  if (!/^[a-zA-Z0-9-]+$/.test(id)) throw new Error('buildEmployeeBadgeHTML: bad EmployeeID');
  return `<span data-at-embedded="" contenteditable="false"> <span tabindex="-1" data-teamio-employeeid="${id}" class="badge mr-xs badge-primary badge-pill mt-1">${escapeHtml(Name)} </span> </span>`;
}

/** The same structure as taskPatients.js buildPatientBadgeHTML (kept import-free on purpose). */
export function buildVoicePatientBadgeHTML({ siteID, PatNum, LName, FName }) {
  const id = String(PatNum == null ? '' : PatNum).trim();
  if (!/^[0-9]+$/.test(id)) throw new Error('buildVoicePatientBadgeHTML: PatNum must be numeric');
  const l = (LName || '').trim();
  const f = (FName || '').trim();
  const label = escapeHtml(l && f ? `${l}, ${f}` : (l || f || `Patient ${id}`));
  const href = `/app/patientprofile/${encodeURIComponent(String(siteID || ''))}-${id}`;
  return `<span data-at-embedded="" contenteditable="false"> <span data-teamio-patientid="${id}" class="badge mr-xs badge-secondary badge-pill mt-1"><a href="${href}" class="">${label}</a></span> </span>`;
}

/**
 * Staff names to hand the recognizer as hints (SpeechRecognition.phrases, on-device contextual
 * biasing) — each first name and each full name, de-duplicated, capped. Patients are NOT included:
 * thousands of names would swamp the hint list.
 */
export function staffPhrases(employees, max = 100) {
  const seen = new Set();
  const out = [];
  for (const e of employees || []) {
    const name = String((e && e.Name) || '').trim().replace(/\s+/g, ' ');
    if (!name) continue;
    for (const p of [name.split(' ')[0], name]) {
      const k = p.toLowerCase();
      if (p.length < 2 || seen.has(k)) continue;
      seen.add(k);
      out.push(p);
    }
  }
  return out.slice(0, max);
}

// Spoken hesitations the recognizer writes down ("Um assign Nilay…"). Only sounds that are never a
// task word: "er" is left alone (ER = emergency room), as are "uh-huh" / "mm-hmm" (they mean yes)
// and all-caps tokens ("UM" can be a name or an acronym).
const FILLER_WORD_RE = /^(?:u+m+|u+h+m*|e+r+m+|h+m+|m{2,}|a+h+)$/i;

/** Drop filler words ("um", "uh", "hmm", "erm", "ah") and the comma Chrome hangs on them. */
export function stripFillers(transcript) {
  const text = String(transcript == null ? '' : transcript).trim();
  const kept = text.split(/\s+/).filter((tok) => {
    const word = tok.replace(/[.,;:!?…]+$/, '');
    const isFiller = FILLER_WORD_RE.test(word) && !(word.length > 1 && word === word.toUpperCase());
    return !isFiller;
  });
  const out = kept.join(' ').replace(/^[,;:\s]+/, '');
  // The recognizer capitalised the first word; if that was the filler, capitalise the new first word.
  return /^[A-Z]/.test(text) && out ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}

function tokenize(text) {
  const tokens = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    tokens.push({ raw: m[0], start: m.index, end: m.index + m[0].length, norm: normalizeWord(m[0]) });
  }
  return tokens;
}

function indexPatients(patients) {
  const byLast = new Map();
  for (const p of patients || []) {
    if (!p || p.PatNum == null) continue;
    const last = normalizeWord(p.LName);
    const first = normalizeWord(String(p.FName || '').split(/\s+/)[0]);
    if (!last) continue;
    if (!byLast.has(last)) byLast.set(last, []);
    byLast.get(last).push({ p, first });
  }
  return byLast;
}

function indexPatientsByFirst(byLast) {
  const byFirst = new Map();
  for (const entries of byLast.values()) {
    for (const e of entries) {
      if (!e.first) continue;
      if (!byFirst.has(e.first)) byFirst.set(e.first, []);
      byFirst.get(e.first).push(e);
    }
  }
  return byFirst;
}

/** Exact surname entries, else entries within edit distance 1 for long-enough surnames. */
function surnameEntries(byLast, word) {
  if (!word) return [];
  if (byLast.has(word)) return byLast.get(word);
  if (word.length < FUZZY_MIN_LEN) return [];
  const out = [];
  for (const [last, entries] of byLast) {
    if (last.length >= FUZZY_MIN_LEN && editDistance(word, last, 1) <= 1) out.push(...entries);
  }
  return out;
}

function staffNameTokens(emp) {
  return String(emp && emp.Name ? emp.Name : '').split(/\s+/).map(normalizeWord).filter(Boolean);
}

/**
 * @param {string} transcript what the recognizer heard
 * @param {{ employees?: object[], patients?: object[], siteID?: string }} ctx
 *   employees: { EmployeeID, Name } (the composer's activeEmployees)
 *   patients:  { PatNum, LName, FName, PatientWithDOB } (customer.patients)
 * @returns {{ html: string, employees: object[], patients: object[], ambiguous: object[] }}
 */
export function resolveVoiceTask(transcript, { employees = [], patients = [], siteID = '' } = {}) {
  const text = stripFillers(transcript);
  const tokens = tokenize(text);
  const used = new Array(tokens.length).fill(false);
  const spans = []; // { from, to (inclusive token indexes), html }
  const result = { html: '', employees: [], patients: [], ambiguous: [] };
  const byLast = indexPatients(patients);
  const taggedPatNums = new Set();

  const claim = (from, to, html, kind = 'patient') => {
    for (let k = from; k <= to; k++) used[k] = true;
    spans.push({ from, to, html, kind });
  };
  const spokenOf = (from, to) => text.slice(tokens[from].start, tokens[to].end);
  const tagPatient = (from, to, p) => {
    claim(from, to, buildVoicePatientBadgeHTML({ siteID, ...p }));
    taggedPatNums.add(Number(p.PatNum));
    result.patients.push({ PatNum: Number(p.PatNum), spoken: spokenOf(from, to) });
  };
  // An ambiguous name stays as plain text; `options` are what the composer offers as one-click picks.
  const markAmbiguous = (from, to, entries) => {
    for (let k = from; k <= to; k++) used[k] = true;
    const shown = entries.slice(0, MAX_OPTIONS);
    result.ambiguous.push({
      kind: 'patient',
      spoken: spokenOf(from, to),
      candidates: shown.map(e => patientLabel(e.p)),
      options: shown.map(e => ({ PatNum: Number(e.p.PatNum), LName: e.p.LName, FName: e.p.FName, label: patientLabel(e.p) })),
      total: entries.length,
    });
  };
  const markStaffAmbiguous = (i, emps) => {
    used[i] = true;
    const shown = emps.slice(0, MAX_OPTIONS);
    result.ambiguous.push({
      kind: 'staff',
      spoken: spokenOf(i, i),
      candidates: shown.map(e => e.Name),
      options: shown.map(e => ({ EmployeeID: e.EmployeeID, Name: e.Name, label: e.Name })),
      total: emps.length,
    });
  };

  // Pass 1 — patient by FIRST LAST ("John Smith").
  for (let i = 0; i + 1 < tokens.length; i++) {
    if (used[i] || used[i + 1]) continue;
    const entries = surnameEntries(byLast, tokens[i + 1].norm).filter(e => e.first && e.first === tokens[i].norm);
    if (entries.length === 1) tagPatient(i, i + 1, entries[0].p);
    else if (entries.length > 1) markAmbiguous(i, i + 1, entries);
  }

  // Pass 2 — staff by full name ("Linda Davis"), longest names first.
  const staff = (employees || []).filter(e => e && e.EmployeeID != null && staffNameTokens(e).length);
  const byLength = staff.slice().sort((a, b) => staffNameTokens(b).length - staffNameTokens(a).length);
  for (const emp of byLength) {
    const parts = staffNameTokens(emp);
    if (parts.length < 2) continue;
    for (let i = 0; i + parts.length <= tokens.length; i++) {
      let ok = true;
      for (let k = 0; k < parts.length; k++) {
        if (used[i + k] || tokens[i + k].norm !== parts[k]) { ok = false; break; }
      }
      if (!ok) continue;
      claim(i, i + parts.length - 1, buildEmployeeBadgeHTML(emp), 'staff');
      result.employees.push({ EmployeeID: emp.EmployeeID, spoken: spokenOf(i, i + parts.length - 1) });
      break;
    }
  }

  // Pass 2b — staff full name with a misheard FIRST name but an exact surname ("Neelay Shah").
  for (let i = 0; i + 1 < tokens.length; i++) {
    if (used[i] || used[i + 1] || !isCapitalised(tokens[i].raw)) continue;
    const key = soundKey(tokens[i].norm);
    if (key.length < 2) continue;
    const hits = staff.filter((e) => {
      const parts = staffNameTokens(e);
      return parts.length === 2 && parts[1] === tokens[i + 1].norm && soundKey(parts[0]) === key;
    });
    if (hits.length !== 1) continue;
    claim(i, i + 1, buildEmployeeBadgeHTML(hits[0]), 'staff');
    result.employees.push({ EmployeeID: hits[0].EmployeeID, spoken: spokenOf(i, i + 1) });
  }

  // Pass 3 — one patient name after "Mrs." / "Dr." / "patient" ("call Mrs. Jones", "patient Jones").
  // After an honorific it is a SURNAME. After "patient" (or "patient named") it is a surname OR a
  // first name ("the patient Justin"); when nobody has that exact name, patients whose first name
  // SOUNDS the same ("Kathy" / Cathy) are offered as picks — never tagged on a guess.
  // "Dr. Patel" is usually the practice's own dentist: a surname that belongs to staff is left alone.
  const staffSurnames = new Set(staff.map(e => staffNameTokens(e)).filter(t => t.length > 1).map(t => t[t.length - 1]));
  const patientsByFirst = indexPatientsByFirst(byLast);
  for (let i = 0; i + 1 < tokens.length; i++) {
    const cue = tokens[i].norm;
    if (!(HONORIFICS.has(cue) || PATIENT_CUES.has(cue))) continue;
    let w = i + 1;
    if (PATIENT_CUES.has(cue) && NAMED_WORDS.has(tokens[w].norm) && w + 1 < tokens.length) w += 1;
    if (used[w] || !tokens[w].norm) continue;
    if ((cue === 'dr' || cue === 'doctor') && staffSurnames.has(tokens[w].norm)) continue;
    let entries = surnameEntries(byLast, tokens[w].norm);
    if (PATIENT_CUES.has(cue)) {
      const seen = new Set(entries.map(e => Number(e.p.PatNum)));
      for (const e of patientsByFirst.get(tokens[w].norm) || []) {
        if (!seen.has(Number(e.p.PatNum))) { seen.add(Number(e.p.PatNum)); entries = entries.concat(e); }
      }
      if (!entries.length && isCapitalised(tokens[w].raw)) {
        const key = soundKey(tokens[w].norm);
        const alike = key.length >= 2 ? [...patientsByFirst.entries()].filter(([first]) => soundKey(first) === key) : [];
        const guesses = alike.flatMap(([, list]) => list);
        if (guesses.length) { markAmbiguous(w, w, guesses); continue; }
      }
    }
    if (!entries.length) continue;
    // "patient John Smith" was already handled by pass 1; here the next word is the one name.
    if (entries.length === 1) tagPatient(w, w, entries[0].p);
    else markAmbiguous(w, w, entries);
  }

  // Pass 4 — staff by FIRST name alone, only after a cue word ("for Nilay", "remind Linda").
  // Exact spelling first; failing that, a unique "sounds like" match ("for Neelay" → Nilay) —
  // unless the heard word is exactly a patient's first name (then it may well be that patient).
  const byFirst = new Map();
  const bySound = new Map();
  for (const emp of staff) {
    const first = staffNameTokens(emp)[0];
    byFirst.set(first, (byFirst.get(first) || []).concat(emp));
    const key = soundKey(first);
    if (key.length >= 2) bySound.set(key, (bySound.get(key) || []).concat(emp));
  }
  const patientFirstNames = new Set();
  for (const entries of byLast.values()) for (const e of entries) if (e.first) patientFirstNames.add(e.first);
  const taggedStaff = new Set(result.employees.map(e => String(e.EmployeeID)));
  for (let i = 1; i < tokens.length; i++) {
    if (used[i] || !STAFF_CUES.has(tokens[i - 1].norm) || !tokens[i].norm) continue;
    let matches = byFirst.get(tokens[i].norm) || [];
    // Sound-alike only for a Capitalised word: the recognizer capitalises names, and this keeps
    // "to call" from becoming staff "Kyle" (both sound like "kl").
    if (!matches.length && isCapitalised(tokens[i].raw) && !patientFirstNames.has(tokens[i].norm)) {
      matches = bySound.get(soundKey(tokens[i].norm)) || [];
    }
    if (!matches.length) continue;
    if (matches.length > 1) { markStaffAmbiguous(i, matches); continue; }
    if (taggedStaff.has(String(matches[0].EmployeeID))) continue;
    claim(i, i, buildEmployeeBadgeHTML(matches[0]), 'staff');
    taggedStaff.add(String(matches[0].EmployeeID));
    result.employees.push({ EmployeeID: matches[0].EmployeeID, spoken: spokenOf(i, i) });
  }

  spans.sort((a, b) => a.from - b.from);

  // Spoken instructions are not the task: "Assign Nilay a task to follow up with Sam" → "Follow up
  // with Sam @Nilay". The person stays assigned through their @ badge, moved to the END of the text
  // (where the Assign chip puts it).
  const frame = commandFrame(tokens, spans);
  if (!frame) {
    result.html = rebuild(text, tokens, spans, 0, tokens.length - 1);
    return result;
  }
  let html = rebuild(text, tokens, spans.filter(s => !frame.moved.includes(s)), frame.first, frame.last);
  if (frame.first > 0) html = html.replace(/^([^<A-Za-z]*)([a-z])/, (m, lead, c) => lead + c.toUpperCase());
  if (frame.moved.length) {
    html = html.replace(/[\s,;:–—-]+$/, '') + ' ' + frame.moved.map(s => s.html).join(' ');
  }
  result.html = html;
  return result;
}

/**
 * Badges replace their spans, everything else is escaped text with its original spacing, for the
 * tokens first..last. A badge swallows the name only — "Smith's," keeps its "'s," after the badge.
 */
function rebuild(text, tokens, spans, first, last) {
  if (!tokens.length) return escapeHtml(text);
  let html = '';
  let cursor = tokens[first].start;
  for (const s of spans) {
    if (s.from < first || s.to > last) continue;
    const suffix = tokens[s.to].raw.match(/(['’]s)?[^A-Za-z0-9]*$/)[0];
    html += escapeHtml(text.slice(cursor, tokens[s.from].start)) + s.html;
    cursor = tokens[s.to].end - suffix.length;
  }
  return html + escapeHtml(text.slice(cursor, tokens[last].end));
}

// Matched against the transcript's words with each TAGGED staff member written as "#s" (a token
// can never normalise to "#"). "me" stands for the speaker — the default assignee, so no badge.
// Each phrase needs the task word or a "to" after the person, so "assign Nilay the Smith chart"
// keeps "the", and "remind Linda about the crown" is left as said.
const INSTRUCTION_LEAD_RE = new RegExp('^(?:(?:please|okay|ok|so|hey|alright) )*(?:(?:can|could|would|will) you )?(?:please )?(?:'
  + 'assign (#s|me) (?:(?:a |an |the )?(?:new )?(?:task|todo|reminder) )?(?:to |for )?'
  + '|assign (?:it |this )?to (#s|me) (?:to )?'
  + '|(?:remind|ask) (#s|me) to '
  + '|tell (#s) (?:to )?'
  + '|have (#s) '
  // "add a task", "new task", "a reminder" — never a bare "task" ("Task the lab with the crown").
  + '|(?:(?:add|create|make) (?:a |an )?(?:new )?|(?:a |an )?new |a |an )(?:task|todo|reminder) (?:for (#s|me) )?(?:to |that )?'
  + ')');
const INSTRUCTION_TAIL_RE = / (?:and )?(?:(?:assign|give) (?:it |this |that )?to|assign) #s$/;

/**
 * The spoken instruction wrapped around the task, if any: { first, last } = the task's own tokens,
 * moved = the staff badges that were inside the instruction (kept, re-added at the end). null when
 * there is no instruction, or when removing it would leave no task at all ("assign Nilay").
 */
function commandFrame(tokens, spans) {
  const items = [];
  const staffAt = new Map(spans.filter(s => s.kind === 'staff').map(s => [s.from, s]));
  for (let i = 0; i < tokens.length; i++) {
    const s = staffAt.get(i);
    if (s) { items.push({ key: '#s', from: i, span: s }); i = s.to; continue; }
    items.push({ key: tokens[i].norm || '_', from: i });
  }
  const keys = items.map(it => it.key);
  let start = 0;
  let end = items.length; // exclusive
  const moved = [];

  const lead = (keys.join(' ') + ' ').match(INSTRUCTION_LEAD_RE);
  if (lead && lead[0]) {
    const n = lead[0].trim().split(' ').length;
    if (n < items.length) {
      start = n;
      items.slice(0, n).forEach(it => { if (it.span) moved.push(it.span); });
    }
  }
  const tail = keys.slice(start).join(' ').match(INSTRUCTION_TAIL_RE);
  if (tail) {
    const n = tail[0].trim().split(' ').length;
    if (end - n > start) {
      end -= n;
      items.slice(end).forEach(it => { if (it.span) moved.push(it.span); });
    }
  }
  if (start === 0 && end === items.length) return null;
  const lastItem = items[end - 1];
  return {
    first: items[start].from,
    last: lastItem.span ? lastItem.span.to : lastItem.from,
    moved,
  };
}
